import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, ArrowRight, Loader2, ArrowUpRight, Leaf } from "lucide-react";
import { useLang } from "@/contexts/LangContext";
import PageMeta from "@/components/PageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RESIDENCE_OPTIONS = ["1-Zimmer", "2-Zimmer", "3-Zimmer", "4-Zimmer+", "Reihenhaus"];
const PRICE_OPTIONS = [
  "200.000 – 350.000 €",
  "350.000 – 500.000 €",
  "500.000 – 700.000 €",
  "700.000 € +",
];

export default function KontaktPage() {
  const { t } = useLang();
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    residence_size: "",
    price_range: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email) {
      toast.error(t("contact.error.required"));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      toast.success(t("contact.success.toast"));
      setForm({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        residence_size: "",
        price_range: "",
        message: "",
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : t("contact.error.generic");
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="kontakt-page" className="pt-32">
      <PageMeta pageKey="contact" />
      <section className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16 pb-16">
        <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-6">{t("contact.eyebrow")}</p>
        <h1 className="font-display text-5xl md:text-7xl lg:text-8xl font-light uppercase tracking-tighter leading-[0.95]">
          {t("contact.title.l1")} <br />
          {t("contact.title.l2")} <span className="italic">{t("contact.title.l2_em")}</span>
        </h1>
        <p className="mt-10 max-w-3xl text-lg md:text-xl text-white/70 font-light leading-relaxed">
          {t("contact.lede")}
        </p>
      </section>

      <section className="border-y border-white/10 grid md:grid-cols-12">
        {/* Info */}
        <div className="md:col-span-5 p-8 md:p-12 lg:p-16 bg-[#0A2244]/40 border-b md:border-b-0 md:border-r border-white/10 space-y-10">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-4">{t("common.address")}</p>
            <div className="flex items-start gap-3 text-white/80">
              <MapPin size={18} strokeWidth={1.5} className="mt-1 shrink-0" />
              <div>
                <p className="font-display text-lg">Royalhouse Bauträger GmbH</p>
                <p className="text-sm text-white/65 mt-1">
                  Bäckerstraße 2/23 <br />
                  A-1010 Wien · Österreich
                </p>
                <p className="text-[11px] text-white/45 mt-2 tracking-wide">UID: ATU77490127</p>
              </div>
            </div>
          </div>

          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-4">{t("common.email")}</p>
            <div className="space-y-2.5">
              <a
                href="mailto:office@royalhouse-building.eu"
                className="flex items-center gap-3 text-white/80 hover:text-white"
                data-testid="kontakt-email-link-1"
              >
                <Mail size={18} strokeWidth={1.5} />
                <span>office@royalhouse-building.eu</span>
              </a>
              <a
                href="mailto:royalhouse.bautraeger@gmail.com"
                className="flex items-center gap-3 text-white/80 hover:text-white"
                data-testid="kontakt-email-link-2"
              >
                <Mail size={18} strokeWidth={1.5} />
                <span>royalhouse.bautraeger@gmail.com</span>
              </a>
            </div>
          </div>

          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-4">{t("common.phone")}</p>
            <a href="tel:+436643289599" className="flex items-center gap-3 text-white/80 hover:text-white">
              <Phone size={18} strokeWidth={1.5} />
              <span>+43 664 328 95 99</span>
            </a>
          </div>

          <div className="border-t border-white/10 pt-8">
            <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-4">{t("common.opening_hours")}</p>
            <ul className="text-sm text-white/65 space-y-1.5">
              <li className="flex justify-between"><span>Mo – Fr</span> <span>09:00 – 18:00</span></li>
              <li className="flex justify-between"><span>Sa</span> <span>10:00 – 14:00</span></li>
              <li className="flex justify-between"><span>So</span> <span>—</span></li>
            </ul>
          </div>
        </div>

        {/* Form */}
        <div className="md:col-span-7 p-8 md:p-12 lg:p-16">
          <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-4">{t("contact.form.eyebrow")}</p>
          <h2 className="font-display text-3xl md:text-5xl font-light uppercase tracking-tight leading-[1.05] mb-10">
            {t("contact.form.title")}
          </h2>

          {submitted ? (
            <div
              className="border border-white/20 p-10 text-center"
              data-testid="kontakt-success"
            >
              <div className="w-14 h-14 mx-auto border border-white flex items-center justify-center mb-6">
                <ArrowRight size={20} strokeWidth={1.5} />
              </div>
              <h3 className="font-display text-2xl md:text-3xl mb-4">{t("contact.success.title")}</h3>
              <p className="text-white/70 leading-relaxed">{t("contact.success.body")}</p>
              <button
                onClick={() => setSubmitted(false)}
                className="mt-8 text-xs tracking-[0.18em] uppercase text-white/60 hover:text-white"
                data-testid="kontakt-new-request"
              >
                {t("contact.success.new")}
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-8" data-testid="kontakt-form">
              <div className="grid md:grid-cols-2 gap-6 md:gap-8">
                <Field label={t("contact.form.first_name")}>
                  <input type="text" value={form.first_name} onChange={(e) => update("first_name", e.target.value)} data-testid="kontakt-first-name" className="rh-input" />
                </Field>
                <Field label={t("contact.form.last_name")}>
                  <input type="text" value={form.last_name} onChange={(e) => update("last_name", e.target.value)} data-testid="kontakt-last-name" className="rh-input" />
                </Field>
                <Field label={t("contact.form.email")}>
                  <input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="kontakt-email" className="rh-input" />
                </Field>
                <Field label={t("contact.form.phone")}>
                  <input type="tel" value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="kontakt-phone" className="rh-input" />
                </Field>
              </div>

              <div className="grid md:grid-cols-2 gap-6 md:gap-8">
                <Field label={t("contact.form.residence_size")}>
                  <select value={form.residence_size} onChange={(e) => update("residence_size", e.target.value)} data-testid="kontakt-residence" className="rh-input rh-select">
                    <option value="">{t("common.choose")}</option>
                    {RESIDENCE_OPTIONS.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                </Field>
                <Field label={t("contact.form.price_range")}>
                  <select value={form.price_range} onChange={(e) => update("price_range", e.target.value)} data-testid="kontakt-price" className="rh-input rh-select">
                    <option value="">{t("common.choose")}</option>
                    {PRICE_OPTIONS.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                </Field>
              </div>

              <Field label={t("contact.form.message")}>
                <textarea
                  rows={5}
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  data-testid="kontakt-message"
                  className="rh-input resize-none"
                  placeholder={t("contact.form.message_placeholder")}
                />
              </Field>

              <button
                type="submit"
                disabled={submitting}
                data-testid="kontakt-submit"
                className="rh-btn-primary inline-flex items-center justify-center gap-3 bg-white text-[#051630] font-bold tracking-[0.18em] uppercase text-xs px-8 py-5 border border-white disabled:opacity-50"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} strokeWidth={2} className="animate-spin" />
                    <span>{t("common.submitting")}</span>
                  </>
                ) : (
                  <>
                    <span>{t("common.send")}</span>
                    <ArrowRight size={16} strokeWidth={2} />
                  </>
                )}
              </button>

              <p className="text-xs text-white/40 leading-relaxed">{t("contact.form.privacy_note")}</p>
            </form>
          )}
        </div>
      </section>

      {/* Map */}
      <section className="border-t border-white/10" data-testid="kontakt-map">
        <div className="aspect-[16/6] md:aspect-[21/6] relative overflow-hidden bg-[#0A2244]">
          <iframe
            title="Royalhouse Standort Wien"
            src="https://www.openstreetmap.org/export/embed.html?bbox=16.36830%2C48.20720%2C16.37730%2C48.21100&layer=mapnik&marker=48.20910%2C16.37280"
            className="w-full h-full grayscale contrast-[1.1]"
            style={{ border: 0, filter: "invert(0.92) hue-rotate(180deg)" }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
          <div className="absolute top-6 left-6 md:top-10 md:left-10 bg-[#051630] border border-white/15 p-5 max-w-xs">
            <p className="text-[11px] tracking-[0.25em] uppercase text-white/50 mb-2">
              Royalhouse HQ
            </p>
            <p className="font-display text-lg leading-snug">
              Bäckerstraße 2/23
              <br />
              1010 Wien
            </p>
            <a
              href="https://www.openstreetmap.org/?mlat=48.2091&mlon=16.3728#map=18/48.2091/16.3728"
              target="_blank"
              rel="noreferrer noopener"
              data-testid="kontakt-map-link"
              className="rh-link mt-3 inline-block text-[11px] tracking-[0.18em] uppercase text-white/70 hover:text-white"
            >
              Auf Karte öffnen →
            </a>
          </div>
        </div>
      </section>

      {/* Well-Being ConTech — banner + CTA */}
      <section
        className="border-t border-white/10 grid md:grid-cols-2"
        data-testid="kontakt-wellbeing-section"
      >
        <div className="relative min-h-[280px] md:min-h-[420px] overflow-hidden">
          <img
            src="/images/wellbeing-hero.jpg"
            alt={t("contact.wellbeing.title")}
            className="absolute inset-0 w-full h-full object-cover"
            data-testid="kontakt-wellbeing-image"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#051630]/70 via-[#051630]/20 to-transparent" />
        </div>
        <div className="bg-[#0A2244]/40 p-8 md:p-12 lg:p-16 flex flex-col justify-center">
          <p className="inline-flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-emerald-200/80 mb-6">
            <Leaf size={13} strokeWidth={1.5} /> {t("contact.wellbeing.eyebrow")}
          </p>
          <h2 className="font-display text-3xl md:text-5xl font-light uppercase tracking-tight leading-[1.05] mb-6">
            {t("contact.wellbeing.title")}
          </h2>
          <p className="text-white/70 leading-relaxed font-light max-w-md mb-10">
            {t("contact.wellbeing.body")}
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <Link
              to="/partner/wellbeing"
              data-testid="kontakt-wellbeing-partner-link"
              className="inline-flex items-center justify-center gap-3 bg-white text-[#051630] font-bold tracking-[0.18em] uppercase text-xs px-7 py-4 border border-white hover:bg-transparent hover:text-white transition-colors"
            >
              <span>{t("contact.wellbeing.cta_partner")}</span>
              <ArrowRight size={16} strokeWidth={1.75} />
            </Link>
            <a
              href="https://wellbeingcontech.com/"
              target="_blank"
              rel="noreferrer noopener"
              data-testid="kontakt-wellbeing-external-link"
              className="rh-link inline-flex items-center gap-2 text-sm tracking-[0.18em] uppercase font-medium text-white/70 hover:text-white"
            >
              {t("contact.wellbeing.cta_external")} <ArrowUpRight size={16} strokeWidth={1.5} />
            </a>
          </div>
        </div>
      </section>

      <style>{`
        .rh-input {
          width: 100%;
          background: transparent;
          border: 0;
          border-bottom: 1px solid rgba(255,255,255,0.2);
          color: #fff;
          padding: 12px 0;
          font-size: 15px;
          font-family: inherit;
          outline: none;
          border-radius: 0;
          transition: border-color 0.3s ease;
        }
        .rh-input:focus {
          border-bottom-color: #fff;
        }
        .rh-input::placeholder { color: rgba(255,255,255,0.35); }
        .rh-select { appearance: none; background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none'><path d='M1 1L6 6L11 1' stroke='white' stroke-opacity='0.5'/></svg>"); background-repeat: no-repeat; background-position: right 0 center; padding-right: 24px; }
        .rh-input option { background: #0A2244; color: #fff; }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] tracking-[0.25em] uppercase text-white/50">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
